import React, { useState } from 'react';

// mui imports ======
import { Box } from '@mui/system';
import { Typography, Divider, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { MuiChipsInput } from 'mui-chips-input';

import DropDownMultiple from '../../../../../../../Components/DropDownMultiple';
import { getChangeLogs } from '../../../../../../../api/changeLogs';

import { toast } from 'react-toastify';
import GeneralButton from '../../../../../../../Components/GeneralButton';
import InputConfig from '../../../../../../../Components/InputConfig';
import constant from '../../../constant.json';
import useStyles from './styles';

/**
 * @name ChangeLogSearch
 * @description Component for searching and filtering change logs based on various criteria.
 * It includes user email, action types, date range, and a feature search input.
 * @returns {JSX.Element} - The rendered component.
 */
const ChangeLogSearch = ({
	updateCurrentSelectedChange,
	filteredFeature,
	updateFilteredFeature,
	updateAllChangeLogs
}) => {
	const { ALL_CHANGE_LOGS_ACTIONS, CHANGE_LOGS } = constant;
	const { siteId } = useParams();
	const classes = useStyles();
	const [userName, setUserName] = useState([]);
	const [actions, setActions] = useState([]);
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [fetchStatus, setFetchStatus] = useState({
		showMessage: false,
		startDate: null,
		endDate: null,
		message: ''
	});

	/**
	 * @name convertToISODate
	 * @description Converts a given date to ISO format and adjusts for timezone offset.
	 * @param {Date|string} date - The date to be converted.
	 * @returns {string} - The date in 'YYYY-MM-DD' format.
	 */
	const convertToISODate = (date) => {
		const localDate = new Date(date);
		localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
		return localDate.toISOString().split('T')[0];
	};

	/**
	 * @name handleFetchChanges
	 * @description Function to handle fetching of change logs based on selected filters.
	 * @returns {void}
	 */
	const handleFetchChanges = async () => {
		const messages = [fromDate === null && 'From Date', toDate === null && 'To Date'].filter(
			Boolean
		);

		if (messages.length) {
			toast.error(`${messages.join(', ')} cannot be left empty.`, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}
		if (fromDate > toDate) {
			toast.error('Invalid Date', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		try {
			const type = CHANGE_LOGS.TYPE;
			const startDate = convertToISODate(fromDate);
			const endDate = convertToISODate(toDate);

			const payload = {
				siteId: siteId,
				startDate: startDate,
				endDate: endDate,
				type,
				users: userName,
				actions: actions.length > 0 ? actions : ALL_CHANGE_LOGS_ACTIONS
			};

			updateAllChangeLogs([]);
			updateCurrentSelectedChange(null);
			setFetchStatus((prevStatus) => ({
				...prevStatus,
				showMessage: true,
				message: 'Loading... Please wait!'
			}));

			const response = await getChangeLogs(payload);
			const { logs } = response.data.data;

			toast.success('Change logs fetched successfully.', {
				position: toast.POSITION.BOTTOM_RIGHT
			});

			updateAllChangeLogs(logs);
			setFetchStatus((prevStatus) => ({ ...prevStatus, showMessage: false, message: '' }));

			if (logs.length === 0) {
				setFetchStatus((prevStatus) => ({
					...prevStatus,
					showMessage: true,
					message: `No logs for the given date range ${new Date(
						fromDate
					).toDateString()} to ${new Date(toDate).toDateString()}`
				}));
			}
		} catch (error) {
			const errorMessage = error?.message || 'An unexpected error occurred.';

			toast.error(errorMessage, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			setFetchStatus((prevStatus) => ({ ...prevStatus, showMessage: false, message: '' }));
		}
	};

	return (
		<Grid container spacing={1} style={{ width: '95%', margin: '4rem 0 1rem 4rem' }}>
			<Grid item xs={3}>
				<Box flexDirection="column" display="flex">
					<Typography fontWeight={600} variant="h6" color="primary.grey">
						User Mail Id
					</Typography>
					<MuiChipsInput
						color="primary"
						value={userName}
						onChange={(users) => {
							setUserName(users);
						}}
						sx={{ width: '40rem' }}
					/>
				</Box>
			</Grid>

			<Grid item xs={3}>
				<DropDownMultiple
					keyData={'ELASTIC_SEARCH_ACTION_STATUS'}
					isObjectEditor={false}
					directValue={actions}
					updateDirectValue={setActions}
					list={ALL_CHANGE_LOGS_ACTIONS}
					label="Action status"
				/>
			</Grid>

			<Grid item xs={3}>
				<LocalizationProvider required dateAdapter={AdapterDayjs}>
					<DatePicker
						label="From Date"
						value={fromDate}
						sx={{ width: '100%', marginTop: '3rem' }}
						onChange={(date) => setFromDate(date)}
						maxDate={dayjs(Date.now())}
					/>
				</LocalizationProvider>
			</Grid>

			<Grid item xs={3}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label="To Date"
						value={toDate}
						sx={{ width: '100%', marginTop: '3rem' }}
						onChange={(date) => setToDate(date)}
						maxDate={dayjs(Date.now())}
					/>
				</LocalizationProvider>
			</Grid>

			<Grid item xs={12} style={{ marginTop: '2rem' }}>
				<GeneralButton
					variant="contained"
					fontWeight={400}
					onClick={handleFetchChanges}
					name="ELASTIC_SEARCH_FETCH_CHANGES"
				>
					Fetch Change Logs
				</GeneralButton>
			</Grid>
			<Grid item xs={12} style={{ margin: '2rem 0 2rem 0' }}>
				<Divider sx={{ width: '100%' }} />
			</Grid>

			<Grid item xs={12}>
				<InputConfig
					keyData={'ELASTIC_SEARCH_FEATURE_SEARCH'}
					inputType="string"
					isObjectEditor={false}
					label="Feature search"
					directValue={filteredFeature}
					updateDirectValue={(value) => {
						updateFilteredFeature(value);
					}}
				/>
			</Grid>
			<Grid item xs={12}>
				{fetchStatus.showMessage && (
					<Box className={classes.sectionContainer}>
						<Typography>{fetchStatus.message}</Typography>
					</Box>
				)}
			</Grid>
		</Grid>
	);
};

export default ChangeLogSearch;
